/**
 * AdminMargens — Painel de análise de margem operacional.
 *
 * Mostra pra cada ação da IA: o que o cliente paga em créditos + R$,
 * o custo real seu (Z-API + Claude + Vision + Whisper + ElevenLabs)
 * e a margem bruta. Também estima o P&L mensal por plano.
 *
 * v0.99.9 · Wave Bonus · Fev/2026 — só leitura, dados vêm do próprio
 * seed de credit_costs + tabela hardcoded de custos reais das APIs.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Coin, TrendUp, WarningCircle, Rocket, ChartBar, ArrowClockwise } from "@phosphor-icons/react";

// Preço-referência do crédito (Plano Essencial: R$ 299 / 5.000)
const CREDIT_PRICE_ESSENCIAL = 299 / 5000;   // R$ 0,0598
const CREDIT_PRICE_OPERACIONAL = 799 / 18000; // R$ 0,0444

// Custos reais em R$ por chamada — fonte: pricing dos providers em Fev/2026.
// Assume USD 5,20 e Emergent LLM Key repassa preço base (0% markup).
const REAL_COSTS = {
  "message.outbound":      { real_brl: 0.024, source: "Z-API por mensagem" },
  "send_message":          { real_brl: 0.024, source: "Z-API por mensagem" },
  "ai.fast_path":          { real_brl: 0.001, source: "Só código (sem LLM)" },
  "ai.orchestrator":       { real_brl: 0.075, source: "Claude Sonnet 4.5 · ~2k in + 500 out" },
  "ai.refine":             { real_brl: 0.030, source: "Claude curto · ~500 tokens" },
  "ai.rewrite_template":   { real_brl: 0.030, source: "Claude curto · ~500 tokens" },
  "ai.help_assistant":     { real_brl: 0.040, source: "Claude médio · ~1k tokens" },
  "ai.episodic_memory":    { real_brl: 0.030, source: "Claude Sonnet · resumo" },
  "campaign.send":         { real_brl: 0.024, source: "Z-API por destinatário" },
  "decision.follow_up":    { real_brl: 0.025, source: "Z-API + trigger" },
  "ai.media_safety":       { real_brl: 0.080, source: "Claude Vision · imagem pequena" },
  "ai.document_classifier":{ real_brl: 0.110, source: "Claude Vision · full doc" },
  "ai.vehicle_vision":     { real_brl: 0.150, source: "Claude Vision + QR/OCR" },
  "ai.audio_whisper":      { real_brl: 0.018, source: "OpenAI Whisper · ~30s" },
  "audio.response":        { real_brl: 0.500, source: "ElevenLabs TTS · ~300 chars" },
  "document_delivery.send":{ real_brl: 0.074, source: "Z-API + storage + LLM breve" },
};

export default function AdminMargens() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/credit-costs");
      const items = Array.isArray(data) ? data : (data?.items || []);
      setRows(items);
    } catch (e) {
      toast.error("Falha ao carregar tabela de créditos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const enriched = useMemo(() => rows.map(r => {
    const real = REAL_COSTS[r.code] || { real_brl: 0, source: "—" };
    const charged_ess = r.cost * CREDIT_PRICE_ESSENCIAL;
    const charged_op = r.cost * CREDIT_PRICE_OPERACIONAL;
    const margin_ess = real.real_brl > 0 ? ((charged_ess - real.real_brl) / real.real_brl) * 100 : 999;
    const margin_op = real.real_brl > 0 ? ((charged_op - real.real_brl) / real.real_brl) * 100 : 999;
    return { ...r, ...real, charged_ess, charged_op, margin_ess, margin_op };
  }), [rows]);

  // P&L mensal estimado (uso 100% dos créditos, mix típico)
  const pnl = useMemo(() => {
    const mix = { messages: 0.40, llm: 0.30, campaign: 0.15, vision: 0.10, audio: 0.05 };
    const avgCostByCategory = {
      messages: 0.024,
      llm: 0.075,
      campaign: 0.024,
      vision: 0.110,
      audio: 0.260,
    };
    const avgCredByCategory = {
      messages: 2, llm: 5, campaign: 2, vision: 5, audio: 11,
    };
    const calc = (totalCred, creditPrice) => {
      let realCost = 0;
      let revenue = 0;
      Object.entries(mix).forEach(([cat, share]) => {
        const catCredits = totalCred * share;
        const calls = catCredits / avgCredByCategory[cat];
        realCost += calls * avgCostByCategory[cat];
        revenue += catCredits * creditPrice;
      });
      return { revenue, realCost, profit: revenue - realCost, margin: ((revenue - realCost) / revenue) * 100 };
    };
    return {
      essential: calc(5000, CREDIT_PRICE_ESSENCIAL),
      operacional: calc(18000, CREDIT_PRICE_OPERACIONAL),
    };
  }, []);

  return (
    <div className="max-w-7xl mx-auto p-6" data-testid="admin-margens-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-600 font-medium mb-1">
            Análise Financeira
          </div>
          <h1 className="font-heading text-2xl font-semibold text-white tracking-tight flex items-center gap-2">
            <ChartBar size={22} className="text-emerald-400" />
            Margens Operacionais
          </h1>
          <p className="text-[12.5px] text-slate-400 mt-0.5">
            Comparativo: <strong className="text-emerald-400">o que cobramos</strong> × <strong className="text-rose-400">o que gastamos</strong> por operação. Uso interno do time financeiro.
          </p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-700 bg-slate-800/60 hover:bg-slate-800 text-[12px] text-slate-300">
          <ArrowClockwise size={12} /> Atualizar
        </button>
      </div>

      {/* Resumo P&L mensal */}
      <div className="grid gap-3 md:grid-cols-2 mb-6">
        <PnlCard title="Plano Essencial" price="R$ 299" credits="5.000 créditos" pnl={pnl.essential} accent="emerald" />
        <PnlCard title="Plano Operacional" price="R$ 799" credits="18.000 créditos" pnl={pnl.operacional} accent="blue" />
      </div>

      {/* Tabela detalhada */}
      <section className="rounded-xl border border-slate-700 bg-slate-900/50 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-700 flex items-center gap-2">
          <Coin size={16} className="text-emerald-400" />
          <div className="font-heading font-semibold text-white text-[14px]">Comparativo por ação</div>
          <div className="ml-auto text-[10.5px] text-slate-500">
            Referência: 1 crédito ≈ R$ {CREDIT_PRICE_ESSENCIAL.toFixed(4)} (Essencial) / R$ {CREDIT_PRICE_OPERACIONAL.toFixed(4)} (Oper.)
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="bg-slate-900 text-[10.5px] uppercase tracking-wider text-slate-400">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium">Ação</th>
                <th className="px-3 py-2.5 text-right font-medium">Créditos</th>
                <th className="px-3 py-2.5 text-right font-medium text-emerald-400">Cobra (Ess)</th>
                <th className="px-3 py-2.5 text-right font-medium text-emerald-400">Cobra (Oper)</th>
                <th className="px-3 py-2.5 text-right font-medium text-rose-400">Custo real</th>
                <th className="px-3 py-2.5 text-right font-medium">Margem Ess</th>
                <th className="px-3 py-2.5 text-right font-medium">Margem Oper</th>
                <th className="px-4 py-2.5 text-left font-medium text-slate-500">Fonte do custo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading && <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-500">Carregando…</td></tr>}
              {!loading && enriched.map(r => {
                const mEss = r.margin_ess;
                const mOp = r.margin_op;
                const badge = (m) => m < 50 ? "text-rose-400 bg-rose-500/10" : m < 100 ? "text-amber-400 bg-amber-500/10" : m < 300 ? "text-slate-300 bg-slate-700/40" : "text-emerald-400 bg-emerald-500/10";
                return (
                  <tr key={r.code} data-testid={`margem-${r.code}`} className="hover:bg-slate-800/40">
                    <td className="px-4 py-2 text-slate-100 font-medium">
                      {r.label}
                      <div className="text-[10.5px] text-slate-500 font-mono">{r.code}</div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-300 font-semibold">{r.cost}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-300">R$ {r.charged_ess.toFixed(3)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-300">R$ {r.charged_op.toFixed(3)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-rose-300">R$ {r.real_brl.toFixed(3)}</td>
                    <td className="px-3 py-2 text-right">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-semibold ${badge(mEss)}`}>
                        {mEss >= 900 ? "∞" : `+${mEss.toFixed(0)}%`}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-semibold ${badge(mOp)}`}>
                        {mOp >= 900 ? "∞" : `+${mOp.toFixed(0)}%`}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-[11px] text-slate-400 italic">{r.source}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Alertas */}
      <div className="mt-6 grid gap-3 md:grid-cols-2">
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
          <div className="flex items-center gap-2 mb-2">
            <WarningCircle size={16} className="text-amber-400" weight="fill" />
            <div className="font-heading font-semibold text-amber-300 text-[13px]">Pontos de atenção</div>
          </div>
          <ul className="text-[11.5px] text-slate-300 space-y-1 list-disc list-inside leading-relaxed">
            <li><strong>Triagem visual</strong> (2 cred = R$ 0,12) — custo R$ 0,08. Margem apertada, sobretudo no Operacional.</li>
            <li><strong>Áudio TTS ElevenLabs</strong> (15 cred = R$ 0,90) — custo R$ 0,50. Sensível a variação do câmbio USD.</li>
          </ul>
        </div>
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Rocket size={16} className="text-emerald-400" weight="fill" />
            <div className="font-heading font-semibold text-emerald-300 text-[13px]">Onde a margem é gigante</div>
          </div>
          <ul className="text-[11.5px] text-slate-300 space-y-1 list-disc list-inside leading-relaxed">
            <li><strong>Fast-path</strong> (SIM/NÃO) — margem quase infinita (custo R$ 0,001).</li>
            <li><strong>Transcrição de áudio Whisper</strong> — +2.500% de margem.</li>
            <li><strong>Envio de documento (PDF)</strong> — +1.185% de margem.</li>
          </ul>
        </div>
      </div>

      <div className="mt-6 text-[10.5px] text-slate-500 leading-relaxed">
        <strong>Nota:</strong> custos reais estimados assumindo USD 5,20 e uso da Emergent LLM Key sem markup. Custo fixo de infra (~R$ 800-1.500/mês) não incluído — dilui em todos os planos.
      </div>
    </div>
  );
}

function PnlCard({ title, price, credits, pnl, accent }) {
  const color = accent === "emerald" ? "emerald" : "blue";
  return (
    <div className={`rounded-xl border border-${color}-500/30 bg-${color}-500/5 p-5`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="font-heading font-semibold text-white text-[15px]">{title}</div>
          <div className="text-[11px] text-slate-400 mt-0.5">{credits}</div>
        </div>
        <div className="text-right">
          <div className={`text-2xl font-bold text-${color}-400 leading-none`}>{price}</div>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">/mês</div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded bg-slate-900/40 p-2">
          <div className="text-[9.5px] uppercase tracking-wider text-slate-500 mb-0.5">Receita</div>
          <div className="text-[13px] font-semibold text-emerald-300 tabular-nums">R$ {pnl.revenue.toFixed(0)}</div>
        </div>
        <div className="rounded bg-slate-900/40 p-2">
          <div className="text-[9.5px] uppercase tracking-wider text-slate-500 mb-0.5">Custo real</div>
          <div className="text-[13px] font-semibold text-rose-300 tabular-nums">R$ {pnl.realCost.toFixed(0)}</div>
        </div>
        <div className="rounded bg-slate-900/40 p-2">
          <div className="text-[9.5px] uppercase tracking-wider text-slate-500 mb-0.5">Lucro</div>
          <div className="text-[13px] font-semibold text-white tabular-nums">R$ {pnl.profit.toFixed(0)}</div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-center gap-1.5">
        <TrendUp size={13} className={`text-${color}-400`} weight="bold" />
        <span className={`text-[12px] text-${color}-300 font-semibold`}>
          Margem bruta: {pnl.margin.toFixed(0)}%
        </span>
      </div>
    </div>
  );
}
